import { registrationFormValues } from "@/components/events/eventFormValues";
import { useState, useCallback } from "react";
import {
  Pencil,
  Trash2,
  Send,
  Plus,
  ImageOff,
  ChevronLeft,
  ChevronRight,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { EventFilters } from "@/components/events/EventFilters";
import { StatusBadge } from "@/components/events/StatusBadge";
import { EventFormFields } from "@/components/events/EventFormFields";
import { EventImageManager } from "@/components/events/EventImageManager";
import { emptyEventFormValues } from "@/components/events/eventFormValues";
import type { EventFormValues } from "@/components/events/eventFormValues";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCard } from "@/components/ui/table-card";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/ui/action-button";
import { Spinner } from "@/components/ui/spinner";
import { ImagePreview } from "@/components/ui/image-preview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  useOrganizerEvents,
  useOrganizerCreateEvent,
  useOrganizerUpdateEvent,
  useOrganizerSubmitEvent,
  useOrganizerDeleteEvent,
  useOrganizerWithdrawEvent,
  useOrganizerVenues,
  useOrganizerCategories,
} from "@/hooks/useOrganizerEvents";
import { useOrganizerProfile } from "@/hooks/useOrganizerProfile";
import { eventDateInput, eventFormDates } from "@/lib/eventDates";
import type { EventResponse, Category, Venue } from "@/types";

function formatDate(dateStr: string) {
  return new Intl.DateTimeFormat("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(dateStr));
}

function CreateEventDialog({
  venues,
  categories,
  defaultSource,
  defaultSourceUrl,
}: {
  venues: Venue[];
  categories: Category[];
  defaultSource: string;
  defaultSourceUrl: string;
}) {
  const createEvent = useOrganizerCreateEvent();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<EventFormValues>(() => ({
    ...emptyEventFormValues(),
    source: defaultSource,
    sourceUrl: defaultSourceUrl,
  }));
  const [createdEvent, setCreatedEvent] = useState<EventResponse | null>(null);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      setCreatedEvent(null);
      setForm({ ...emptyEventFormValues(), source: defaultSource, sourceUrl: defaultSourceUrl });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dates = eventFormDates(form);
      if (!dates.date) throw new Error("Uzupełnij datę rozpoczęcia.");
      const result = await createEvent.mutateAsync({
        title: form.title,
        eventDescription: form.eventDescription,
        date: dates.date,
        endDate: dates.endDate,
        isAllDay: form.isAllDay,
        venueId: form.venueId,
        categoryId: form.categoryId,
        detailsUrl: form.detailsUrl,
        registrationOptions: form.registrationOptions,
        registrationNotes: form.registrationNotes,
        source: form.source,
        sourceUrl: form.sourceUrl,
      });
      toast.success("Wydarzenie dodane jako szkic");
      setCreatedEvent(result);
    } catch (err) {
      toast.error("Nie udało się dodać wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Dodaj wydarzenie
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{createdEvent ? "Dodaj zdjęcia" : "Nowe wydarzenie"}</DialogTitle>
        </DialogHeader>
        {createdEvent ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Wydarzenie zostało utworzone jako szkic. Możesz teraz dodać zdjęcia.
            </p>
            <EventImageManager eventId={createdEvent.id} eventsBasePath="/organizer/events" />
            <DialogFooter>
              <Button type="button" onClick={() => handleOpenChange(false)}>
                Gotowe
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <EventFormFields
              values={form}
              onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
              categories={categories}
              venues={venues}
              hideSourceFields
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Anuluj
              </Button>
              <Button
                type="submit"
                disabled={createEvent.isPending || !form.venueId || !form.categoryId}
              >
                {createEvent.isPending ? "Dodawanie..." : "Dodaj szkic"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditEventDialog({
  event,
  open,
  onOpenChange,
  venues,
  categories,
}: {
  event: EventResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venues: Venue[];
  categories: Category[];
}) {
  const updateEvent = useOrganizerUpdateEvent();
  const [form, setForm] = useState<EventFormValues>(() => ({
    title: event.title,
    eventDescription: event.eventDescription,
    date: eventDateInput(event.date, event.isAllDay),
    endDate: eventDateInput(event.endDate, event.isAllDay),
    isAllDay: event.isAllDay,
    venueId: event.venueId ?? "",
    categoryId: event.categoryId ?? "",
    detailsUrl: event.detailsUrl,
    ...registrationFormValues(event),
    source: event.source,
    sourceUrl: event.sourceUrl,
    isPreview: event.isPreview,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dates = eventFormDates(form);
      if (!dates.date) throw new Error("Uzupełnij datę rozpoczęcia.");
      await updateEvent.mutateAsync({
        id: event.id,
        body: {
          title: form.title,
          eventDescription: form.eventDescription,
          date: dates.date,
          endDate: dates.endDate,
          isAllDay: form.isAllDay,
          venueId: form.venueId,
          categoryId: form.categoryId,
          detailsUrl: form.detailsUrl,
          registrationOptions: form.registrationOptions,
          registrationNotes: form.registrationNotes,
          source: form.source,
          sourceUrl: form.sourceUrl,
        },
      });
      toast.success("Wydarzenie zaktualizowane");
      onOpenChange(false);
    } catch (err) {
      toast.error("Nie udało się zaktualizować wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edytuj wydarzenie</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <EventFormFields
            values={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
            categories={categories}
            venues={venues}
            hideSourceFields
            afterFields={
              <EventImageManager eventId={event.id} eventsBasePath="/organizer/events" />
            }
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Anuluj
            </Button>
            <Button
              type="submit"
              disabled={updateEvent.isPending || !form.venueId || !form.categoryId}
            >
              {updateEvent.isPending ? "Zapisywanie..." : "Zapisz"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrganizerEventRow({
  event,
  categories,
  venues,
}: {
  event: EventResponse;
  categories: Category[];
  venues: Venue[];
}) {
  const submitEvent = useOrganizerSubmitEvent();
  const withdrawEvent = useOrganizerWithdrawEvent();
  const deleteEvent = useOrganizerDeleteEvent();
  const [editOpen, setEditOpen] = useState(false);
  const category = categories.find((c) => c.id === event.categoryId);
  const isDraft = event.status === "draft";
  const isPending = event.status === "pending";

  const handleSubmit = async () => {
    try {
      await submitEvent.mutateAsync(event.id);
      toast.success("Wydarzenie zgłoszone do przeglądu");
    } catch (err) {
      toast.error("Nie udało się zgłosić wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleWithdraw = async () => {
    try {
      await withdrawEvent.mutateAsync(event.id);
      toast.success("Wydarzenie wycofane do szkicu");
    } catch (err) {
      toast.error("Nie udało się wycofać wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success("Wydarzenie usunięte");
    } catch (err) {
      toast.error("Nie udało się usunąć wydarzenia", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      <TableRow>
        <TableCell className="w-[70px] min-w-[70px]">
          {event.coverImage ? (
            <ImagePreview src={event.coverImage.thumb} alt={event.title} />
          ) : (
            <div className="w-[70px] h-[70px] bg-slate-100 rounded flex items-center justify-center">
              <ImageOff className="h-5 w-5 text-slate-400" />
            </div>
          )}
        </TableCell>
        <TableCell className="max-w-xs">
          <p className="font-medium truncate">{event.title}</p>
        </TableCell>
        <TableCell className="whitespace-nowrap">{formatDate(event.date)}</TableCell>
        <TableCell className="max-w-32 truncate">{event.locationName}</TableCell>
        <TableCell>
          <StatusBadge status={event.status} rejectionReason={event.rejectionReason} />
        </TableCell>
        <TableCell>{category?.name ?? "—"}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {isDraft && (
              <>
                <ActionButton
                  tooltip="Edytuj"
                  className="h-8 w-8"
                  onClick={() => setEditOpen(true)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </ActionButton>
                <ActionButton
                  tooltip="Zgłoś do przeglądu"
                  className="h-8 w-8 text-blue-600 hover:text-blue-700"
                  onClick={handleSubmit}
                  disabled={submitEvent.isPending}
                >
                  {submitEvent.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <Send className="h-3.5 w-3.5" />
                  )}
                </ActionButton>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <ActionButton
                      tooltip="Usuń"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      disabled={deleteEvent.isPending}
                    >
                      {deleteEvent.isPending ? (
                        <Spinner className="h-3.5 w-3.5" />
                      ) : (
                        <Trash2 className="h-3.5 w-3.5" />
                      )}
                    </ActionButton>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Usuń wydarzenie</AlertDialogTitle>
                      <AlertDialogDescription>
                        Czy na pewno chcesz usunąć wydarzenie „{event.title}"? Tej operacji nie
                        można cofnąć.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anuluj</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDelete}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Usuń
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
            {isPending && (
              <ActionButton
                tooltip="Wycofaj do szkicu"
                className="h-8 w-8 text-amber-600 hover:text-amber-700"
                onClick={handleWithdraw}
                disabled={withdrawEvent.isPending}
              >
                {withdrawEvent.isPending ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <Undo2 className="h-3.5 w-3.5" />
                )}
              </ActionButton>
            )}
          </div>
        </TableCell>
      </TableRow>
      {isDraft && (
        <EditEventDialog
          event={event}
          open={editOpen}
          onOpenChange={setEditOpen}
          venues={venues}
          categories={categories}
        />
      )}
    </>
  );
}

export default function OrganizerEventsPage() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("all");
  const [hidePast, setHidePast] = useState(true);
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const cursor = cursorStack.at(-1);
  const {
    data: result,
    isLoading,
    error,
  } = useOrganizerEvents({
    status,
    search,
    categoryId: categoryId === "all" ? undefined : categoryId,
    hidePast,
    cursor,
    limit,
  });

  const events = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const { data: categories = [] } = useOrganizerCategories();
  const { data: venues = [] } = useOrganizerVenues();
  const { data: profile } = useOrganizerProfile();

  const handleStatusChange = (v: string) => {
    setStatus(v);
    setCursorStack([]);
  };
  const handleCategoryChange = (v: string) => {
    setCategoryId(v);
    setCursorStack([]);
  };
  const handleHidePastChange = (v: boolean) => {
    setHidePast(v);
    setCursorStack([]);
  };
  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };

  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));

  const page = cursorStack.length + 1;

  return (
    <div>
      <PageHeader
        title="Moje wydarzenia"
        action={
          venues.length > 0 ? (
            <CreateEventDialog
              venues={venues}
              categories={categories}
              defaultSource={profile?.displayName ?? ""}
              defaultSourceUrl={profile?.displayUrl ?? ""}
            />
          ) : (
            <Button disabled>
              <Plus className="h-4 w-4 mr-2" />
              Dodaj wydarzenie
            </Button>
          )
        }
      />
      <EventFilters
        search={search}
        onSearchChange={handleSearchChange}
        status={status}
        onStatusChange={handleStatusChange}
        categoryId={categoryId}
        onCategoryChange={handleCategoryChange}
        categories={categories}
        hidePast={hidePast}
        onHidePastChange={handleHidePastChange}
        includeDraftStatus
      />

      {venues.length === 0 && !isLoading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 mb-4">
          Nie masz przypisanych lokalizacji. Skontaktuj się z administratorem, aby przypisał Ci
          lokalizacje — dopiero wtedy będziesz mógł dodawać wydarzenia.
        </div>
      )}

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white animate-pulse border border-black/5" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania: {error.message}</p>}
      {!isLoading && events.length === 0 && (
        <p className="text-center py-12 text-muted-foreground">
          Brak wydarzeń spełniających kryteria.
        </p>
      )}
      {!isLoading && events.length > 0 && (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[70px]"></TableHead>
                <TableHead>Tytuł</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Lokalizacja</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Kategoria</TableHead>
                <TableHead className="w-28">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <OrganizerEventRow
                  key={event.id}
                  event={event}
                  categories={categories}
                  venues={venues}
                />
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!isLoading && (events.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
